import { isPubstackUserRole } from '@pubstack/common/src/rolesAndFeatures'
import { useAtomValue } from 'jotai'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { currentScopeState } from '~/state'
import { staffAccessState } from './useNodeStaffAccess'
import { useScope } from './useScope'

const SPARK_FOR_PUB_FEATURE = 'sparkForPub'

/**
 * Whether the current scope has been onboarded on the Spark For Pub feature
 * (i.e. a scope-level spark-for-pub stack has been provisioned by the Spark team).
 *
 * The provisioning predicate: it says nothing about who may see Spark for Pubs. A viewer-facing surface
 * asks {@link useSparkForPubStaffAccess}.
 */
export const useIsSparkForPubOnboarded = (): boolean => {
  const currentScope = useAtomValue(currentScopeState)
  return currentScope.onboardedFeatures?.includes(SPARK_FOR_PUB_FEATURE) ?? false
}

type SparkForPubScope = Parameters<typeof staffAccessState>[0]
type SparkForPubViewer = Parameters<typeof staffAccessState>[1] & { userRoles?: Parameters<typeof isPubstackUserRole>[0] }

/**
 * Spark for Pubs (`sparkForPub`) for the person looking, from the scope's two toggles. See {@link staffAccessState}.
 *
 * - `canCreateSeat`: the seat only exists once onboarded; access (or staff on a staff-only scope) opens it.
 * - `isDashboardEnabled`: the analytics dashboard stays Pubstack-only on top of that.
 */
export const sparkForPubStaffState = (scope: SparkForPubScope, user: SparkForPubViewer) => {
  const { hasAccess, has, isStaffOnly, isStaffOnProduct, isLockedToStaff } = staffAccessState(scope, user, SPARK_FOR_PUB_FEATURE)
  const isOnboarded = scope.onboardedFeatures?.includes(SPARK_FOR_PUB_FEATURE) ?? false
  const canCreateSeat = isOnboarded && has
  return {
    hasSparkForPubAccess: hasAccess,
    isStaffOnly,
    isStaffOnSparkForPub: isStaffOnProduct,
    isLockedToStaff,
    isOnboarded,
    canCreateSeat,
    isDashboardEnabled: canCreateSeat && !!user.userRoles && isPubstackUserRole(user.userRoles),
  }
}

/**
 * Spark for Pubs for the person looking. Onboarded with user access off is the staff-only state: Pubstack
 * admins use it as on any scope that has it (with a shield), everybody else finds it not available.
 */
export const useSparkForPubStaffAccess = () => sparkForPubStaffState(useScope(), useAuthenticatedUser())

/**
 * Whether the Spark For Publishers dashboard should be visible / accessible: the scope is onboarded, the
 * viewer has Spark for Pubs (access, or admin on a staff-only scope) AND is a Pubstack (internal) user.
 * The dashboard is gated to Pubstack users while the feature is being validated.
 */
export const useIsSparkForPubUiEnabled = (): boolean => useSparkForPubStaffAccess().isDashboardEnabled

/** Whether the current user can create Spark For Pub seats for the current scope: onboarded, and access or staff-on. */
export const useCanCreateSparkForPubSeat = (): boolean => useSparkForPubStaffAccess().canCreateSeat
